import { ReactNode, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../constants'

type AccordionPropTypes = {
  title: string
  isExpanded: boolean
  onAccordionClick: () => void
  /** Overrides the accessible name when the visible header includes more than title (e.g. caption). */
  accessibleName?: string
  icon?: string
  iconClassName?: string
  iconComponent?: ReactNode
  isIconBorder?: boolean
  middleContent?: ReactNode
  middleContentClassName?: string
  middleContentTitleClassName?: string
  isBorderTop?: boolean
  accordionClassName?: string
  accordionHeaderClassName?: string
  accordionBodyClassName?: string
  headerAccordionBgColorClassName?: string
  chevronTopPadding?: string
  /** When true, scrolls expanded content into view on user expand (false → true). Default true for MF detail flows. */
  scrollOnExpand?: boolean
  children?: ReactNode
}

export default function MFAccordion({
  title,
  isExpanded,
  onAccordionClick,
  accessibleName,
  icon,
  iconClassName,
  iconComponent,
  isIconBorder,
  middleContent,
  middleContentClassName,
  middleContentTitleClassName,
  isBorderTop,
  accordionClassName,
  accordionHeaderClassName,
  accordionBodyClassName,
  headerAccordionBgColorClassName = 'bg-[#FFFFFF]',
  chevronTopPadding = '30%',
  scrollOnExpand = true,
  children
}: AccordionPropTypes) {
  /* TODO: Rewrite the whole component'S STYLES Remove heights and paddings
  IMPACT: L2 AND ELITE LANDING PAGES */
  const contentRef = useRef<HTMLDivElement>(null)
  const prevIsExpanded = useRef(isExpanded)
  // When mounted already expanded, skip enter animation until after a collapse.
  const shouldAnimateEnterRef = useRef(!isExpanded)
  // Hint only in aria-description (Details_B06 / Result_B07) — not in aria-label — or TalkBack announces it twice.
  const accordionHint = isExpanded ? 'Double tap to collapse' : 'Double tap to expand'

  useEffect(() => {
    if (
      scrollOnExpand &&
      isExpanded &&
      !prevIsExpanded?.current &&
      contentRef?.current
    ) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    prevIsExpanded.current = isExpanded
  }, [isExpanded, scrollOnExpand])

  useEffect(() => {
    if (!isExpanded) {
      shouldAnimateEnterRef.current = true
    }
  }, [isExpanded])

  return (
    <div
      className={`flex flex-col w-full ${accordionClassName ?? ''} ${isBorderTop ? 'border-t border-[#EBF2F2]' : ''}`}
    >
      <button
        type='button'
        onClick={onAccordionClick}
        // Power Rebalancing_B05: state once via aria-expanded only — never in aria-label
        // (TalkBack would announce "Collapsed, Label, Collapsed").
        aria-expanded={isExpanded}
        aria-label={accessibleName ?? title}
        aria-description={accordionHint}
        className={`flex w-full text-left border-none outline-none focus:outline-none focus:border-none active:outline-none active:border-none ${headerAccordionBgColorClassName} min-h-[74px] py-[16px] px-[20px] justify-between 
        cursor-pointer ${accordionHeaderClassName ?? ''}`}
      >
        <div aria-hidden='true' className='flex flex-1 w-1 items-center gap-[16px]'>
          <AccordionIcon
            icon={icon}
            iconClassName={iconClassName}
            iconComponent={iconComponent}
            isIconBorder={isIconBorder}
          />
          <div className={`flex w-1 flex-1 items-center pr-8 ${middleContentClassName ?? ''}`}>
            {middleContent ?? (
              <div className={`text-[#232323] text-[14px] font-[500] ${middleContentTitleClassName ?? ''}`}>
                {title}
              </div>
            )}
          </div>
        </div>
        <span className='flex' aria-hidden='true'>
          <AccordionChevron isExpanded={isExpanded} chevronTopPadding={chevronTopPadding} />
        </span>
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={shouldAnimateEnterRef.current ? { height: 0 } : false}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ type: 'spring', duration: 0.4, bounce: 0 }}
            ref={contentRef}
            className={`overflow-hidden w-full px-[20px] text-[14px] leading-[150%] ${accordionBodyClassName}`}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Helper functions

export function AccordionIcon({
  icon,
  iconClassName,
  iconComponent,
  isIconBorder
}: {
  icon?: string
  iconClassName?: string
  iconComponent?: ReactNode
  isIconBorder?: boolean
}) {
  if (iconComponent) {
    return <>{iconComponent}</>
  }

  if (!icon) {
    return null
  }

  return (
    <div
      className={`w-[40px] h-[40px] mt-[2px] 
        ${isIconBorder ? 'border border-[#EBF2F2] rounded-[12px] overflow-hidden w-[41px] h-[41px]' : ''}
        ${iconClassName ?? ''}`}
    >
      <img src={icon} alt='' aria-hidden='true' />
    </div>
  )
}

export function AccordionChevron({
  isExpanded,
  chevronTopPadding
}: {
  isExpanded: boolean
  chevronTopPadding: string
}) {
  return (
    <div className='flex flex-col items-center'>
      <div style={{ height: chevronTopPadding }} className='w-[3px]'></div>
      <motion.div
        initial={false}
        animate={{ rotate: isExpanded ? 180 : 0 }}
        transition={{ duration: 0.35 }}
        className='w-4 h-4'
      >
        <img src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/chevron-${'collapsed'}-icon.svg`} alt='' aria-hidden='true' />
      </motion.div>
    </div>
  )
}
